/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  productOptions = [],
  onSearchProducts = () => { },
}) => {
  const { user } = useUser();
  const toDateInputValue = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

      const dmyMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (dmyMatch) {
        const dd = String(dmyMatch[1]).padStart(2, "0");
        const mm = String(dmyMatch[2]).padStart(2, "0");
        const yyyy = dmyMatch[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const unitOfProductSoldObj = initialData?.unitOfProductSold || {};

  const initialFormData = {
    productMasterId: initialData?.productMasterId || initialData?.productId || "",
    productName: initialData?.productName || initialData?.masterProduct?.productName || "",
    productIdOrSku:
      initialData?.productIdOrSku || initialData?.productId || initialData?.sku || "",
    dateOfSale: toDateInputValue(initialData?.dateOfSale),
    unitOfProductSold: unitOfProductSoldObj?.value || "",
    unitOfProductSoldUnit:
      initialData?.masterProduct?.soldUnit ||
      unitOfProductSoldObj?.unit ||
      "",
    averageAnnualEnergyConsumptionPerUnit:
      initialData?.averageAnnualEnergyConsumptionPerUnit ||
      initialData?.avgAnnualEnergyConsumptionPerUnit ||
      "",
    typicalOperatingHours: initialData?.typicalOperatingHours || "",
    typeOfMaintenanceRequired: initialData?.typeOfMaintenanceRequired || "",
    recyclableComponentsPercentage:
      initialData?.recyclableComponentsPercentage || initialData?.replacementParts || "",
    designedForReuseRecyclingDisposal:
      initialData?.designedForReuseRecyclingDisposal ||
      initialData?.isProductDesignedForReuseRecyclingDisposal ||
      "",
    documents: initialData?.documents || [],
    editRemarkReason: initialData?.editRemarkReason || "",
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [productSearchText, setProductSearchText] = useState("");
  const inlineErrorTextProps = {
    sx: {
      ml: 0,
      mr: 0,
      mt: "4px",
      fontSize: "0.95rem",
      lineHeight: 1.2,
    },
  };

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setProductSearchText(initialFormData.productName || "");
  }, [isOpen, initialData]);

  /** Ensures edit row product appears in the list when `masterProducts` is stale/empty after bulk upload. */
  const productOptionsWithFallback = useMemo(() => {
    const base = Array.isArray(productOptions) ? [...productOptions] : [];
    const mp = initialData?.masterProduct;
    const id = String(
      formData.productMasterId || initialData?.productId || mp?._id || ""
    ).trim();
    const sku = String(
      formData.productIdOrSku || initialData?.productIdOrSku || initialData?.sku || mp?.productId || ""
    ).trim();
    const name = String(
      formData.productName || initialData?.productName || mp?.productName || mp?.name || ""
    ).trim();

    if (!id && !sku && !name) return base;

    const alreadyIn = base.some(
      (o) =>
        (id && String(o?._id || o?.id) === id) ||
        (sku && (o?.productId === sku || o?.sku === sku)) ||
        (name && String(o?.productName || o?.name || "").trim() === name)
    );
    if (alreadyIn) return base;

    const stub = {
      ...(mp && typeof mp === "object" ? mp : {}),
      _id: id || mp?._id,
      id: id || mp?._id,
      productId: sku || mp?.productId,
      productName: name || mp?.productName || mp?.name || sku || "Product",
    };
    return [stub, ...base];
  }, [
    productOptions,
    initialData?._id,
    initialData?.productId,
    initialData?.productName,
    initialData?.productIdOrSku,
    initialData?.masterProduct,
    formData.productMasterId,
    formData.productIdOrSku,
    formData.productName,
  ]);

  useEffect(() => {
    const selectedProduct = productOptionsWithFallback.find(
      (option) =>
        (option?._id || option?.id) === formData.productMasterId ||
        (option?.productId && option?.productId === formData.productIdOrSku) ||
        (option?.productName || option?.name) === formData.productName
    );

    if (selectedProduct) {
      const selectedName = selectedProduct?.productName || selectedProduct?.name || "";
      const selectedSku = selectedProduct?.productId || selectedProduct?.sku || "";
      const selectedMasterId = selectedProduct?._id || selectedProduct?.id || "";
      const soldUnit = selectedProduct?.soldUnit || "";

      setProductSearchText(selectedName);
      setFormData((prev) => ({
        ...prev,
        productMasterId: selectedMasterId || prev.productMasterId,
        productName: selectedName || prev.productName,
        productIdOrSku: selectedSku || prev.productIdOrSku,
        ...(soldUnit ? { unitOfProductSoldUnit: soldUnit } : {}),
      }));
    }
  }, [
    productOptionsWithFallback,
    formData.productMasterId,
    formData.productIdOrSku,
    formData.productName,
  ]);

  const getSelectedMasterFromOptions = () =>
    productOptionsWithFallback.find(
      (option) =>
        (option?._id || option?.id) === formData.productMasterId ||
        (option?.productId && option?.productId === formData.productIdOrSku) ||
        (option?.productName || option?.name) === formData.productName
    );

  const resolveSoldUnit = () => {
    const fromList = String(getSelectedMasterFromOptions()?.soldUnit || "").trim();
    if (fromList) return fromList;

    const fromForm = String(formData.unitOfProductSoldUnit || "").trim();
    if (fromForm) return fromForm;

    const masterId = initialData?.masterProduct?._id;
    const currentId = formData.productMasterId;
    if (masterId && currentId && String(masterId) === String(currentId)) {
      return String(initialData?.masterProduct?.soldUnit || "").trim();
    }
    return "";
  };

  const fieldConfig = [
    {
      label: "Product Name",
      name: "productName",
      type: "text",
      required: true,
      placeholder: "Enter product name",
    },
    {
      label: "Product ID / SKU",
      name: "productIdOrSku",
      type: "text",
      required: true,
      placeholder: "Enter product ID / SKU",
      disabled: true,
    },
    {
      label: "Date of Sale",
      name: "dateOfSale",
      type: "date",
      required: true,
    },
    {
      label: "Unit of Product Sold (Quantity)",
      name: "unitOfProductSold",
      type: "number",
      required: true,
      placeholder: "Enter quantity",
      unitFieldName: "unitOfProductSoldUnit",
      unitDisabled: true,
    },


    // {
    //   label: "Average Annual Energy Consumption per Unit",
    //   name: "averageAnnualEnergyConsumptionPerUnit",
    //   type: "number",
    //   required: true,
    //   placeholder: "Enter value",
    // },
    // {
    //   label: "Typical Operating Hours per Day / Week / Year",
    //   name: "typicalOperatingHours",
    //   type: "text",
    //   required: true,
    //   placeholder: "Enter hours",
    // },
    {
      label: "Type of Maintenance Required (optional)",
      name: "typeOfMaintenanceRequired",
      type: "text",
      // required: true,
      placeholder: "Enter maintenance type",
    },
    {
      label: "Recyclable Components % (optional)",
      name: "recyclableComponentsPercentage",
      type: "number",
      // required: true,
      placeholder: "Enter recyclable components",
      // disabled: true,
    },
    {
      name: "editRemarkReason",
      type: "multlyline",
      placeholder: "Additional edit remarks",
      require: true,
    },
    // {
    //   label: "Is the Product Designed for Reuse, Recycling, or Disposal?",
    //   name: "designedForReuseRecyclingDisposal",
    //   type: "text",
    //   // required: true,
    //   placeholder: "Enter details",
    // },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      if (field?.name === "editRemarkReason") {
        return;
      }

      const value = formData[field.name];

      if (field.required) {
        if (value === "" || value === null || value === undefined) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if (field.unitFieldName) {
        const unitVal = resolveSoldUnit();
        if (unitVal === "" || unitVal === null || unitVal === undefined) {
          newErrors[field.unitFieldName] = `${field.label} unit is required.`;
        }
      }

      if (field.type === "number" && value !== "" && isNaN(Number(value))) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }

      if (
        field.name === "recyclableComponentsPercentage" &&
        value !== "" &&
        Number(value) > 100
      ) {
        newErrors[field.name] = `${field.label} cannot be greater than 100.`;
      }
    });

    if (user?.endUser && isEditing) {
      const remark = String(formData?.editRemarkReason ?? "").trim();
      if (!remark) {
        newErrors.editRemarkReason = "Edit remark is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (
      name === "recyclableComponentsPercentage" &&
      value !== "" &&
      Number(value) > 100
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const resolveProductField = (selected, keys = []) => {
    for (const key of keys) {
      if (selected?.[key] !== undefined && selected?.[key] !== null) {
        return selected[key];
      }
    }
    return "";
  };

  const applySelectedProduct = (selected) => {
    if (!selected) return;

    const pickOrPrev = (resolvedValue, previousValue) =>
      resolvedValue === "" || resolvedValue === null || resolvedValue === undefined
        ? previousValue
        : resolvedValue;

    setFormData((prev) => ({
      ...prev,
      productMasterId: pickOrPrev(
        resolveProductField(selected, ["_id", "id"]),
        prev.productMasterId
      ),
      productName: pickOrPrev(
        resolveProductField(selected, ["productName", "name"]),
        prev.productName
      ),
      productIdOrSku: pickOrPrev(
        resolveProductField(selected, ["productId", "sku"]),
        prev.productIdOrSku
      ),
      averageAnnualEnergyConsumptionPerUnit: pickOrPrev(
        resolveProductField(selected, [
          "averageAnnualEnergyConsumptionPerUnit",
          "avgAnnualEnergyConsumptionPerUnit",
        ]),
        prev.averageAnnualEnergyConsumptionPerUnit
      ),
      typicalOperatingHours: pickOrPrev(
        resolveProductField(selected, ["typicalOperatingHours"]),
        prev.typicalOperatingHours
      ),
      typeOfMaintenanceRequired: pickOrPrev(
        resolveProductField(selected, ["typeOfMaintenanceRequired"]),
        prev.typeOfMaintenanceRequired
      ),
      recyclableComponentsPercentage: pickOrPrev(
        resolveProductField(selected, [
          "recyclableComponentsPercentage",
          "replacementParts",
        ]),
        prev.recyclableComponentsPercentage
      ),
      designedForReuseRecyclingDisposal: pickOrPrev(
        resolveProductField(selected, [
          "designedForReuseRecyclingDisposal",
          "isProductDesignedForReuseRecyclingDisposal",
        ]),
        prev.designedForReuseRecyclingDisposal
      ),
      unitOfProductSoldUnit: pickOrPrev(
        resolveProductField(selected, ["soldUnit"]),
        prev.unitOfProductSoldUnit
      ),
    }));

    setErrors((prev) => ({
      ...prev,
      productName: "",
      productIdOrSku: "",
      averageAnnualEnergyConsumptionPerUnit: "",
      typicalOperatingHours: "",
      typeOfMaintenanceRequired: "",
      recyclableComponentsPercentage: "",
      designedForReuseRecyclingDisposal: "",
    }));
  };

  const clearAutoFilledProductData = () => {
    setFormData((prev) => ({
      ...prev,
      productMasterId: "",
      productName: "",
      productIdOrSku: "",
      unitOfProductSoldUnit: "",
      averageAnnualEnergyConsumptionPerUnit: "",
      typicalOperatingHours: "",
      typeOfMaintenanceRequired: "",
      recyclableComponentsPercentage: "",
      designedForReuseRecyclingDisposal: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    let formDataToSubmit = {
      ...formData,
      unitOfProductSoldUnit: resolveSoldUnit(),
      documents: uploadedFiles,
    };

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
      formDataToSubmit.editRemarkReason = formData.editRemarkReason;
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => {
              if (field?.name === "editRemarkReason" && !(user?.endUser && isEditing)) {
                return null;
              }

              return (
                <div key={field.name} style={{ margin: "1rem 0" }}>
                  {user?.endUser && isEditing && field?.type === "multlyline" ? (
                    <Multiline
                      title={formatTitleWithOptional("Edit Remark Reason")}
                      placeholder="Additional edit remarks"
                      fieldName="editRemarkReason"
                      value={formData.editRemarkReason}
                      handleChange={handleChange("editRemarkReason")}
                      errors={errors.editRemarkReason}
                    />
                  ) : field.type === "docs" ? (
                    <FileField
                      title={formatTitleWithOptional("Upload Documents (optional)")}
                      name={field.name}
                      errors={errors[field.name]}
                      fileUrls={uploadedFiles}
                      progress={uploadProgress[field.name] || []}
                      {...{
                        setUploadedFiles,
                        setUploadProgress,
                        setErrors,
                      }}
                    />
                  ) : field.name === "productName" ? (
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        {formatTitleWithOptional(field.label)}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={productOptionsWithFallback}
                        value={
                          productOptionsWithFallback.find(
                            (option) =>
                              (option?._id || option?.id) === formData.productMasterId ||
                              (option?.productId &&
                                option?.productId === formData.productIdOrSku) ||
                              (option?.productName || option?.name) === formData.productName
                          ) || null
                        }
                        inputValue={productSearchText}
                        onInputChange={(event, newInputValue, reason) => {
                          setProductSearchText(newInputValue);
                          if (reason === "input" || reason === "clear") {
                            onSearchProducts(newInputValue);
                          }
                        }}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            applySelectedProduct(newValue);
                          } else {
                            clearAutoFilledProductData();
                          }
                          setProductSearchText(
                            newValue?.productName || newValue?.name || ""
                          );
                        }}
                        getOptionLabel={(option) =>
                          option?.productName || option?.name || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          (option?._id || option?.id) === (value?._id || value?.id)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Tap to Select"
                            error={Boolean(errors[field.name])}
                            helperText={errors[field.name] || ""}
                            FormHelperTextProps={inlineErrorTextProps}
                          />
                        )}
                        sx={{
                          width: "100%",
                          "& .MuiAutocomplete-inputRoot": {
                            height: 40,
                            alignItems: "center",
                          },
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        {formatTitleWithOptional(field.label)}
                      </Typography>

                      {field.unitFieldName ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.75rem",
                            alignItems: "flex-start",
                            minWidth: 0,
                          }}
                        >
                          <TextField
                            type={"text"}
                            size="small"
                            required={field.required}
                            placeholder={field.placeholder || ""}
                            value={formData[field.name] || ""}
                            onChange={handleChange(field.name, field.type)}
                            error={Boolean(errors[field.name])}
                            helperText={errors[field.name] || ""}
                            FormHelperTextProps={inlineErrorTextProps}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              "& .MuiFormHelperText-root": { mx: 0 },
                            }}
                          />
                          <TextField
                            select
                            size="small"
                            sx={{
                              flex: "0 0 auto",
                              minWidth: "6.75rem",
                              maxWidth: "36%",
                              "& .MuiOutlinedInput-root": {
                                minWidth: "6.75rem",
                              },
                              "& .MuiSelect-select": {
                                overflow: "visible",
                                textOverflow: "clip",
                                whiteSpace: "nowrap",
                                paddingLeft: "10px",
                                paddingRight: "28px",
                              },
                            }}
                            required
                            value={resolveSoldUnit() || ""}
                            onChange={handleChange(field.unitFieldName, "select")}
                            disabled={Boolean(field.unitDisabled)}
                            error={Boolean(errors[field.unitFieldName])}
                            helperText={errors[field.unitFieldName] || ""}
                            FormHelperTextProps={inlineErrorTextProps}
                            SelectProps={{ displayEmpty: true }}
                            InputProps={{
                              sx: field.unitDisabled
                                ? {
                                  backgroundColor: "#f3f4f6",
                                  "& .MuiSelect-select.Mui-disabled": {
                                    WebkitTextFillColor: "#6b7280",
                                  },
                                }
                                : {},
                            }}
                          >
                            {(() => {
                              const selectedMaster = getSelectedMasterFromOptions();
                              const masterId = initialData?.masterProduct?._id;
                              const currentId = formData.productMasterId;
                              const snapshotUnit =
                                masterId &&
                                  currentId &&
                                  String(masterId) === String(currentId)
                                  ? initialData?.masterProduct?.soldUnit
                                  : "";
                              const unitMenuOptions = Array.from(
                                new Set(
                                  [
                                    formData[field.unitFieldName],
                                    selectedMaster?.soldUnit,
                                    snapshotUnit,
                                  ].filter(
                                    (u) =>
                                      u !== null &&
                                      u !== undefined &&
                                      String(u).trim() !== ""
                                  )
                                )
                              );
                              const display = resolveSoldUnit() || "";
                              const optionsList =
                                unitMenuOptions.length > 0
                                  ? unitMenuOptions
                                  : display
                                    ? [display]
                                    : [];

                              if (optionsList.length === 0) {
                                return (
                                  <MenuItem value="" disabled>
                                    Unit
                                  </MenuItem>
                                );
                              }
                              return optionsList.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ));
                            })()}
                          </TextField>
                        </Box>
                      ) : field.type === "select" ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          required={field.required}
                          value={formData[field.name] || ""}
                          onChange={handleChange(field.name, field.type)}
                          error={Boolean(errors[field.name])}
                          helperText={errors[field.name] || ""}
                          FormHelperTextProps={inlineErrorTextProps}
                          SelectProps={{
                            displayEmpty: true,
                          }}
                        >
                          <MenuItem value="" disabled>
                            Tap to select
                          </MenuItem>
                          {field.options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          type={field.type === "date" ? "date" : "text"}
                          size="small"
                          fullWidth
                          required={field.required}
                          placeholder={field.placeholder || ""}
                          value={formData[field.name] || ""}
                          onChange={handleChange(field.name, field.type)}
                          disabled={Boolean(field.disabled)}
                          error={Boolean(errors[field.name])}
                          helperText={errors[field.name] || ""}
                          FormHelperTextProps={inlineErrorTextProps}
                          sx={{
                            ...(field?.disabled && {
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#6b7280",
                                cursor: "not-allowed",
                              },
                              "& .MuiInputBase-root.Mui-disabled": {
                                backgroundColor: "#f3f4f6",
                              },
                            }),
                          }}
                          inputProps={
                            field.type === "date"
                              ? { max: new Date().toISOString().split("T")[0] }
                              : field.name === "recyclableComponentsPercentage"
                                ? { min: 0, max: 100, inputMode: "decimal" }
                                : {}
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

